// Admin -> Features, under the "In-app bug reports" switch: every reporter's reports, newest
// first. GitHub delivery is parked (no egress), so this is where the team reads them. Text was
// scrubbed and checked against the recording's transcript before it was stored; diagnostics are
// the allowlisted set. Rendered as text only, never as HTML.
import { useId, useState } from 'react';
import type { AdminBugReport, BugReportDiagnostics, BugReportStatus } from '@scribe/core/types';
import { useAdminBugReports } from '../lib/queries';
import { pathOf } from '../routes';
import { Badge, Pagination, formatDate } from './ui';

const PAGE_SIZE = 25;

const STATUS_LABEL: Record<BugReportStatus, string> = {
  pending: 'New',
  delivering: 'Sending',
  delivered: 'Filed on GitHub',
  failed: 'Delivery failed',
};

const STATUS_BADGE: Record<BugReportStatus, string> = {
  pending: 'pending',
  delivering: 'pending',
  delivered: 'success',
  failed: 'danger',
};

export function BugReportsPanel() {
  const filterId = useId();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<BugReportStatus | ''>('');
  const query = useAdminBugReports({ page, page_size: PAGE_SIZE, ...(status ? { status } : {}) });
  const reports = query.data?.items ?? [];

  return (
    <div className="bug-reports-panel">
      <div className="toolbar">
        <label className="muted" htmlFor={filterId}>
          Show
        </label>
        <select
          id={filterId}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as BugReportStatus | '');
            setPage(1);
          }}
        >
          <option value="">All reports</option>
          <option value="pending">New</option>
          <option value="delivered">Filed on GitHub</option>
          <option value="failed">Delivery failed</option>
        </select>
        {query.data && (
          <span className="count">
            {query.data.page_info.total} report{query.data.page_info.total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {query.isLoading && <p className="muted">Loading bug reports…</p>}
      {query.isError && (
        <div className="banner banner-danger">
          <p>Couldn't load bug reports.</p>
          <button className="sm" onClick={() => void query.refetch()}>
            Retry
          </button>
        </div>
      )}
      {query.data && reports.length === 0 && (
        <p className="empty-state">{status ? 'No reports with this status.' : 'No bug reports yet.'}</p>
      )}

      {reports.length > 0 && (
        <ul className="bug-report-list">
          {reports.map((r) => (
            <BugReportItem key={r.id} report={r} />
          ))}
        </ul>
      )}

      {query.data && <Pagination pageInfo={query.data.page_info} onPageChange={setPage} />}
    </div>
  );
}

function BugReportItem({ report }: { report: AdminBugReport }) {
  return (
    <li className="bug-report">
      <div className="bug-report-head">
        <Badge kind={STATUS_BADGE[report.status]}>{STATUS_LABEL[report.status]}</Badge>
        <span className="muted">
          {formatDate(report.created_at)} · {report.reporter_display_name || 'Unknown member'} · {report.area}
          {report.failure_code ? ` · ${report.failure_code}` : ''}
        </span>
      </div>
      <p className="bug-report-text">{report.what_happened}</p>
      <details>
        <summary>Details</summary>
        <dl className="bug-report-details">
          {report.expected && (
            <>
              <dt>Expected</dt>
              <dd>{report.expected}</dd>
            </>
          )}
          {report.steps && (
            <>
              <dt>Steps</dt>
              <dd className="bug-report-text">{report.steps}</dd>
            </>
          )}
          {report.recording_id && (
            <>
              <dt>Recording</dt>
              <dd>
                <a href={pathOf({ name: 'recording', id: report.recording_id })}>Open recording</a>
              </dd>
            </>
          )}
          {report.github_issue_url && (
            <>
              <dt>GitHub</dt>
              <dd>
                <a href={report.github_issue_url} target="_blank" rel="noopener noreferrer">
                  Issue #{report.github_issue_number}
                </a>
              </dd>
            </>
          )}
          <dt>Diagnostics</dt>
          <dd>
            <Diagnostics value={report.diagnostics} />
          </dd>
        </dl>
      </details>
    </li>
  );
}

function Diagnostics({ value }: { value: BugReportDiagnostics }) {
  const lines: string[] = [];
  for (const key of ['app_version', 'build', 'browser', 'os', 'route'] as const) {
    if (value[key]) lines.push(`${key}: ${value[key]}`);
  }
  for (const e of value.client_errors ?? []) {
    lines.push(`error ${e.code} at ${e.at}${e.status != null ? ` (HTTP ${e.status})` : ''}`);
  }
  if (value.request_ids?.length) lines.push(`request ids: ${value.request_ids.join(', ')}`);
  return lines.length ? <pre className="bug-report-diagnostics">{lines.join('\n')}</pre> : <span className="muted">None</span>;
}
